import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Image,
  InputRightAddon,
  Link as ChakraLink,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useBreakpointValue,
  useColorMode,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, Info, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import {
  LuAtSign,
  LuBriefcase,
  LuCircleUserRound,
  LuFlag,
  LuHash,
  LuIdCard,
  LuKeyRound,
  LuLandPlot,
  LuMap,
  LuMapPin,
  LuMapPinHouse,
  LuMessageSquareMore,
  LuMoon,
  LuPhone,
  LuSun,
} from 'react-icons/lu';
import { Link, useHistory, useParams } from 'react-router-dom';
import { z } from 'zod';
import zxcvbn from 'zxcvbn';
import logoBlue from '../../assets/SignIn/logo.png';
import logoWhite from '../../assets/SignIn/logo.svg';
import { Input } from '../../components/shared/Input';
import Steps from '../../components/shared/Steps';
import { useAuth } from '../../hooks/auth';
import backend from '../../services/backend';

const CalculaForcaSenha = (senha: string) => {
  let score = 0;

  const scores = [
    { score: 0, title: 'Senha fraca', cor: 'red.500', w: '1%' },
    { score: 1, title: 'Senha média', cor: 'orange.500', w: '20%' },
    { score: 2, title: 'Senha média', cor: 'orange.500', w: '40%' },
    { score: 3, title: 'Senha aceitável', cor: 'blue.500', w: '60%' },
    { score: 4, title: 'Boa senha', cor: 'teal.500', w: '80%' },
    { score: 5, title: 'Senha forte', cor: 'green.500', w: '100%' },
  ];

  if (!senha) return scores[0]; // caso seja vazio

  // Critérios de pontuação
  if (senha.length >= 6) score += 1; // mínimo 6 caracteres
  if (senha.length >= 10) score += 1; // bônus para 10+ caracteres
  if (/[a-z]/.test(senha)) score += 1; // letra minúscula
  if (/[A-Z]/.test(senha)) score += 1; // letra maiúscula
  if (/\d/.test(senha)) score += 1; // número
  if (/[!@#$%^&*(),.?":{}|<>]/.test(senha)) score += 1; // caractere especial

  if (score >= scores.length) score = scores.length - 1;
  return scores[score];
};

function SenhaTemForcaMinima(senha: string): boolean {
  return CalculaForcaSenha(senha)?.score > 2;
}

const createAccountFormSchema = z
  .object({
    email: z.string().nonempty('E-mail obrigatório').email('E-mail inválido'),
    name: z.string().nonempty('Nome obrigatório'),
    cpf: z.string().nonempty('Cpf obrigatório'),
    cnpj: z.string().optional(),
    telefone: z.string().nonempty('Telefone obrigatório'),
    password: z
      .string()
      .nonempty('Senha obrigatória')
      .min(6, 'No mínimo 6 caracteres')
      .refine(senha => SenhaTemForcaMinima(senha), {
        message: 'senha precisa ser mais forte',
      }),
    passwordConfirmation: z
      .string()
      .nonempty('Confirmação de senha obrigatória')
      .min(6, 'No mínimo 6 caracteres')
      .refine(senha => SenhaTemForcaMinima(senha), {
        message: 'senha precisa ser mais forte',
      }),
    cep: z.string().nonempty('obrigatório'),
    uf: z.string().nonempty('obrigatório'),
    cidade: z.string().nonempty('obrigatório'),
    logradouro: z.string().nonempty('obrigatório'),
    numero: z.string().nonempty('obrigatório'),
    bairro: z.string().nonempty('obrigatório'),
    complemento: z.string().optional(),
  })
  .refine(data => data.password === data.passwordConfirmation, {
    message: 'As senhas não conferem',
    path: ['passwordConfirmation'],
  });
type CreateAccountFormData = z.infer<typeof createAccountFormSchema>;

const CreateAccount: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState,
    control,
    trigger,
    setValue,
    watch,
  } = useForm<CreateAccountFormData>({
    resolver: zodResolver(createAccountFormSchema),
  });

  const { isSubmitting, errors } = formState;

  const isWideVersion = useBreakpointValue({
    base: false,
    sm: true,
    md: true,
    lg: true,
  });

  const { colorMode, toggleColorMode } = useColorMode();

  // estilos
  const bg = useColorModeValue('blue.600', 'gray.800');
  const bgWideVersion = useColorModeValue('white', 'gray.800');
  const bgForm = useColorModeValue('white', '#2D3748');
  const bgFormWideVersion = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('gray', 'white');
  const colorIcon = useColorModeValue('#3182ce', 'white');
  const link = useColorModeValue('blue.600', 'white');
  const bgPasswordBackground = useColorModeValue('gray.200', 'gray.500');
  const detailPassword = useColorModeValue('#171923', 'white');

  const history = useHistory();
  const toast = useToast();
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const params: { idConvite: string } = useParams();

  const valorCEP = watch('cep');
  const buscaCEP = async () => {
    if (buscandoCEP) return;
    if (valorCEP?.length < 9 || valorCEP === '') {
      toast({
        title: `Insira um CEP válido`,
        status: 'info',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }
    setBuscandoCEP(true);
    try {
      const response = await backend.get('/cep', {
        params: { cep: valorCEP },
      });
      const dados = response.data;
      setValue('logradouro', dados?.logradouro);
      setValue('complemento', dados?.complemento);
      setValue('bairro', dados?.bairro);
      setValue('cidade', dados?.localidade);
      setValue('uf', dados?.uf);
      toast({
        title: `Sucesso`,
        status: 'success',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (error) {
      toast({
        title: error?.response?.data?.message || 'Erro ao buscar CEP',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setBuscandoCEP(false);
    }
  };

  const options = [
    { step: 1, title: 'Dados' },
    { step: 2, title: 'Endereço' },
    { step: 3, title: 'Acesso' },
  ];
  const [stepAtual, setStepAtual] = useState(1);

  const stepFields = {
    1: ['name', 'telefone', 'cpf', 'cnpj'],
    2: ['cep', 'uf', 'cidade', 'logradouro', 'numero', 'bairro', 'complemento'],
    3: ['email', 'password', 'passwordConfirmation'],
  };

  const AvancarPara = async (nextStep: number) => {
    const camposStepAtual = stepFields[stepAtual];
    const valid = await trigger(camposStepAtual as any);
    if (valid) {
      setStepAtual(nextStep);
    }
  };

  const handleCreateAccount: SubmitHandler<CreateAccountFormData> = async ({
    email,
    name,
    password,
    passwordConfirmation,
    cpf,
    cnpj,
    telefone,
    cep,
    logradouro,
    bairro,
    cidade,
    uf,
    numero,
    complemento,
  }) => {
    try {
      const response = await backend.post('/usuario', {
        email,
        name,
        password,
        passwordConfirmation,
        cpf,
        cnpj,
        telefone,
        cep,
        logradouro,
        bairro,
        cidade,
        uf,
        numero,
        complemento,
        idConvite: params.idConvite,
      });

      history.push('/');
    } catch (error: any) {
      toast({
        title: 'Erro ao criar conta:',
        description:
          error?.response?.data?.message ||
          error?.response?.data ||
          error?.response?.message ||
          'Erro na criação da conta',
        status: 'error',
        duration: 9000,
        isClosable: true,
        position: 'bottom-right',
      });
    }
  };

  const senha = watch('password');
  const senha2 = watch('passwordConfirmation');
  const forca = useMemo(() => CalculaForcaSenha(senha), [senha]);
  const forca2 = useMemo(() => CalculaForcaSenha(senha2), [senha2]);
  const tipColor = useColorModeValue('white', 'gray.700');

  const BarraForca = ({ forca }) => (
    <Flex
      bg={bgPasswordBackground}
      borderTopRadius="0px"
      borderBottomRadius="8px"
      w="100%"
      position="relative"
    >
      <Flex
        borderTopRadius="0px"
        borderBottomRadius="8px"
        bg={forca?.cor}
        w={forca?.w}
        h="100%"
        zIndex={0}
        justify="space-between"
        position="absolute"
        top="0px"
        left="0px"
        transition="width 0.5s ease-in-out"
      />
      <Flex w="100%" justify="space-between" mx="2" my="4px">
        <Text color={detailPassword} fontSize="small" zIndex={1} ml="2">
          {forca?.title}
        </Text>
        <Flex zIndex={1}>
          <Tooltip
            rounded="2xl"
            label={
              <Box p="2" rounded="4xl">
                <Text color={tipColor}>Como deixar sua senha forte:</Text>
                <Text color={tipColor}>- Mais que 6 caracteres</Text>
                <Text color={tipColor}>- Ao menos uma letra minuscula</Text>
                <Text color={tipColor}>- Ao menos uma letra maiuscula</Text>
                <Text color={tipColor}>- Ao menos um número</Text>
                <Text color={tipColor}>- Ao menos um caractere especial</Text>
              </Box>
            }
            placement="right-end"
          >
            <Info color={detailPassword} />
          </Tooltip>
        </Flex>
      </Flex>
    </Flex>
  );

  return (
    <Flex
      w="100vw"
      h="100vh"
      flexDir="column"
      align="center"
      justify={['flex-start', 'center']}
      bg={[bgWideVersion, bg]}
    >
      {(isWideVersion || colorMode !== 'light') && (
        <Image src={logoWhite} alt="Okton - Sistemas Empresariais" my="8" />
      )}

      {colorMode === 'light' && !isWideVersion && (
        <Image
          src={logoBlue}
          alt="Okton - Sistemas Empresariais"
          my="8"
          w="250px"
        />
      )}

      <Flex
        as="form"
        maxWidth={488}
        flexDir="column"
        p="8"
        rounded="2xl"
        boxShadow={['none', 'xs']}
        bg={[bgFormWideVersion, bgForm]}
        onSubmit={handleSubmit(handleCreateAccount)}
      >
        <Stack spacing="0px">
          <Flex justify="space-between">
            <div />
            <Text
              color={color}
              fontSize="sm"
              fontWeight="medium"
              align="center"
            >
              Criar a sua conta
            </Text>

            <Icon
              as={colorMode === 'light' ? LuSun : LuMoon}
              color={colorIcon}
              fontSize={[18, 22]}
              onClick={toggleColorMode}
            />
          </Flex>
          <Flex>
            <Steps data={options} atual={stepAtual} />
          </Flex>
          {stepAtual === 1 && (
            <>
              <Input
                type="text"
                label="Digitar nome completo*"
                error={errors.name}
                leftIcon={LuCircleUserRound}
                {...register('name')}
              />

              <Input
                type="text"
                label="Digitar telefone*"
                error={errors.telefone}
                leftIcon={LuPhone}
                {...register('telefone')}
                mask="(00) 00000-0000"
              />

              <Input
                type="text"
                label="Digitar CPF*"
                error={errors.cpf}
                leftIcon={LuIdCard}
                {...register('cpf')}
                mask="000.000.000-00"
              />

              <Input
                type="text"
                label="Digitar CNPJ"
                error={errors.cnpj}
                leftIcon={LuBriefcase}
                {...register('cnpj')}
                mask="00.000.000/0000-00"
              />
            </>
          )}
          {stepAtual === 2 && (
            <>
              <SimpleGrid templateColumns={['1fr', '1fr', '1fr 1fr']} gap="4">
                <Flex>
                  <Input
                    type="text"
                    label="Digitar CEP*"
                    error={errors.cep}
                    leftIcon={LuMapPin}
                    {...register('cep')}
                    borderTopRightRadius="0px"
                    borderBottomRightRadius="0px"
                    mask="00000-000"
                    onChange={e => setValue('cep', e.target.value)}
                    onBlur={e => setValue('cep', e.target.value)}
                  />
                  <Button
                    alignSelf="flex-end"
                    h="48px"
                    variant="outline"
                    isLoading={buscandoCEP}
                    spinner={<Spinner />}
                    borderTopLeftRadius="0px"
                    borderBottomLeftRadius="0px"
                    onClick={buscaCEP}
                  >
                    <Search />
                  </Button>
                </Flex>
                <Input
                  type="text"
                  label="Escolher UF*"
                  error={errors.uf}
                  leftIcon={LuFlag}
                  {...register('uf')}
                />
              </SimpleGrid>
              <Input
                type="text"
                label="Escolher cidade*"
                error={errors.cidade}
                leftIcon={LuMap}
                {...register('cidade')}
              />
              <Input
                type="text"
                label="Digitar logradouro*"
                error={errors.logradouro}
                leftIcon={LuMapPinHouse}
                {...register('logradouro')}
              />
              <SimpleGrid templateColumns={['1fr', '1fr', '1fr 1fr']} gap="4">
                <Input
                  type="text"
                  label="Digitar número*"
                  error={errors.numero}
                  leftIcon={LuHash}
                  {...register('numero')}
                />
                <Input
                  type="text"
                  label="Digitar bairro*"
                  error={errors.bairro}
                  leftIcon={LuLandPlot}
                  {...register('bairro')}
                />
              </SimpleGrid>
              <Input
                type="text"
                label="Digitar complemento*"
                error={errors.complemento}
                leftIcon={LuMessageSquareMore}
                {...register('complemento')}
              />
            </>
          )}
          {stepAtual === 3 && (
            <>
              <Input
                type="email"
                label="Digitar email para login*"
                error={errors.email}
                leftIcon={LuAtSign}
                {...register('email')}
              />

              <Input
                type="password"
                label="Digitar a senha"
                error={errors.password}
                {...register('password')}
                borderBottomRadius="0px"
                strength
              />
              <BarraForca forca={forca} />

              <Input
                type="password"
                label="Confirmar a senha"
                error={errors.passwordConfirmation}
                {...register('passwordConfirmation')}
                borderBottomRadius="0px"
                strength
              />
              <BarraForca forca={forca2} />

              {/* <PasswordInput
                name="password"
                control={control}
                label="Digitar a senha*"
                error={errors.password}
              /> */}

              {/* <PasswordInput
                name="passwordConfirmation"
                control={control}
                label="Confirmar a senha*"
                error={errors.passwordConfirmation}
              /> */}
            </>
          )}

          {stepAtual === 3 && (
            <Flex>
              <Text color={color} fontSize="xs" fontWeight="light" mt="20px">
                Ao me inscrever, aceito os{' '}
                <Link to="/">
                  <ChakraLink
                    as="span"
                    color="blue.500"
                    fontSize="xs"
                    fontWeight="light"
                  >
                    Termos de serviço da Okton
                  </ChakraLink>
                </Link>{' '}
                e concordo com a{' '}
                <Link to="/">
                  <ChakraLink
                    as="span"
                    color="blue.500"
                    fontSize="xs"
                    fontWeight="light"
                  >
                    Política de Privacidade
                  </ChakraLink>
                </Link>
                .
              </Text>
            </Flex>
          )}
        </Stack>

        {stepAtual === 1 && (
          <Button
            mt="6"
            onClick={() => {
              AvancarPara(2);
            }}
          >
            Avançar
            <ArrowRight />
          </Button>
        )}
        {stepAtual === 2 && (
          <Flex justify="space-between">
            <Button
              mt="6"
              variant="outline"
              w="49%"
              onClick={() => setStepAtual(1)}
            >
              <ArrowLeft />
              Voltar
            </Button>
            <Button mt="6" w="49%" onClick={() => AvancarPara(3)}>
              Avançar
              <ArrowRight />
            </Button>
          </Flex>
        )}
        {stepAtual === 3 && (
          <Flex justify="space-between">
            <Button
              mt="6"
              variant="outline"
              w="49%"
              onClick={() => setStepAtual(2)}
            >
              <ArrowLeft />
              Voltar
            </Button>
            <Button type="submit" mt="6" isLoading={isSubmitting} w="50%">
              Registre-se
            </Button>
          </Flex>
        )}

        <Divider my="6" borderColor={color} />

        <HStack justify="center">
          <Link to="/">
            <ChakraLink as="div" color={link} fontSize="sm">
              Já tem uma conta da Okton? Entrar
            </ChakraLink>
          </Link>
        </HStack>
      </Flex>
    </Flex>
  );
};

export default CreateAccount;
